import { Capacitor } from '@capacitor/core';

import {
  getAnalyticsConsent,
  subscribeToConsentChanges,
  type ConsentPreferences,
} from '@shared/lib/consent';

/**
 * GA4 (gtag.js) live consent sync.
 *
 * The base Google tag is installed statically in `index.html` using Google
 * Consent Mode v2: it loads on every web page (so Google can detect the tag)
 * but consent defaults to "denied", so GA stays cookieless until the user opts
 * in. `index.html` also applies any consent choice already stored from a
 * previous visit.
 *
 * This module's job is only the *live* part: when the user changes their
 * consent during the session (accept/reject in the consent banner), push a
 * Consent Mode `update` so GA starts or stops using storage immediately —
 * `analytics_storage` follows analytics consent, the ad_* signals follow
 * marketing consent. Web-only; a no-op on the native shell or when the base
 * tag was not installed (unconfigured measurement id).
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Map the platform consent preferences to Consent Mode v2 signals. */
function consentSignals(prefs: ConsentPreferences): Record<string, 'granted' | 'denied'> {
  const ads = prefs.marketing ? 'granted' : 'denied';
  return {
    analytics_storage: prefs.analytics ? 'granted' : 'denied',
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  };
}

function isGtagAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    !Capacitor.isNativePlatform() &&
    typeof window.gtag === 'function'
  );
}

function currentPagePath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function trackAnalyticsPageView(path = currentPagePath()): void {
  if (!isGtagAvailable()) return;

  const pageUrl = new URL(path, window.location.origin);
  const pagePath = `${pageUrl.pathname}${pageUrl.search}${pageUrl.hash}`;

  window.gtag?.('event', 'page_view', {
    page_location: pageUrl.href,
    page_path: pagePath,
    page_title: document.title,
  });
}

/**
 * Subscribe GA's consent state to live consent changes. Call once on web app
 * startup. No-op on native, or when the base tag is absent (`window.gtag`
 * undefined because the measurement id is not configured).
 */
export function initAnalytics(): void {
  if (!isGtagAvailable()) return;

  let analyticsConsentGranted = getAnalyticsConsent().analytics === true;

  subscribeToConsentChanges((prefs) => {
    window.gtag?.('consent', 'update', consentSignals(prefs));
    if (prefs.analytics && !analyticsConsentGranted) {
      trackAnalyticsPageView();
    }
    analyticsConsentGranted = prefs.analytics === true;
  });
}
